/** Guess who: a real question. Tap a name and the right one lights up with its number. */

import { useRef, useState, type MouseEvent, type ReactNode, type SyntheticEvent } from "react";

import { Avatar, Fit, Scene, W, Words, Zone, useStageExtra, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import { lookFor, tokenValue, wr } from "../../theme";
import type { Chat } from "../../types";

type Question = Chat["quiz"][number];
type Props = Question & { onDone: () => void; index: number; of: number; me: string | null };
type Tap = { option: string; x: number; y: number };

const TOP = 720;
const STEP = 178;
const PILL = 150;

// The story moves on when its card is tapped; answering must not count as that tap.
const keep = (event: SyntheticEvent) => event.stopPropagation();

/** The card's colours: light and dark cards get the video's two blob sets. */
function looksOf(index: number, options: number) {
  const look = lookFor(`quiz-${index}`);
  const dark = look.fg === wr("white");
  const onYellow = look.bg === wr("yellow");
  const blobs: BlobSpec[] = [
    { colour: dark ? "fuchsia" : "blurple", x: 1000, y: 1800, r: 280, seed: 18.4 + options, wob: 0.18 },
    { colour: dark ? "cyan" : onYellow ? "fuchsia" : "yellow", x: 40, y: 1820, r: 110, seed: 19.2, wob: 0.24 },
  ];
  // The right answer turns yellow, which would vanish on the yellow card.
  return { look, dark, blobs, win: onYellow ? "green" : "yellow" };
}

/** "pixelfox: 1,204 messages between 2 and 5 am" → "1,204" and what it counts. */
function splitStat(stat: string, answer: string) {
  const rest = stat.startsWith(`${answer}: `) ? stat.slice(answer.length + 2) : stat;
  const found = /^([\d.,]+)\s+(.+)$/.exec(rest);
  return found ? { n: found[1], unit: found[2] } : { n: null, unit: rest };
}

/** Where the finger landed, in stage pixels; a key press lands on the pill itself, `drop` below its designed place. */
function tapPoint(event: MouseEvent<HTMLElement>, i: number, drop: number) {
  const box = event.currentTarget.closest(".wr-stage")?.getBoundingClientRect();
  if (event.detail === 0 || !box?.width) return { x: 800, y: TOP + i * STEP + PILL / 2 + drop };
  const scale = box.width / W;
  return { x: (event.clientX - box.left) / scale, y: (event.clientY - box.top) / scale };
}

export function Quiz({ question, options, answer, stat, onDone, index, of, me }: Props) {
  const [tap, setTap] = useState<Tap | null>(null);
  const pills = useRef<(HTMLButtonElement | null)[]>([]);
  const shown = options.slice(0, 4);
  const { look, dark, blobs, win } = looksOf(index, shown.length);
  const parts = splitStat(stat, answer);
  // The pills sit in the middle zone, this far below their designed place.
  const drop = Math.round(useStageExtra() / 2);

  const root = useTimeline((s) => {
    s.rise(".kick", 0.3, { y: 0 });
    s.words(".q-q", 0.4);
    s.tl.fromTo(
      "[data-part=slot]",
      { x: 200, autoAlpha: 0 },
      { x: 0, autoAlpha: 1, duration: 0.45, stagger: 0.08, ease: "power4.out" },
      0.85,
    );
    s.sound("whoosh", 0.85, 1, { pan: 0.6, length: 0.5 });
  });

  const stateOf = (option: string) => {
    if (!tap) return "idle";
    if (option === answer) return "right";
    return option === tap.option ? "wrong" : "out";
  };

  return (
    <div ref={root} className="contents">
      <Scene blobs={blobs}>
        <Zone at="top">
          <div className="kick" style={dark ? { color: wr("yellow") } : undefined}>
            {of > 1 ? `Guess who · ${index} of ${of}` : "Guess who"}
          </div>
          <div className="q-q disp">
            <Words text={question} />
          </div>
        </Zone>
        <Zone at="middle">
          {shown.map((option, i) => {
            const state = stateOf(option);
            return (
              // The slot slides in; the pill inside it lights up or fades on the answer.
              <div key={option} data-part="slot" className="abs" style={{ left: 72, top: TOP + i * STEP, width: 936, height: PILL }}>
                <button
                  type="button"
                  ref={(el) => {
                    pills.current[i] = el;
                  }}
                  className="opt"
                  aria-label={option}
                  data-state={state}
                  style={{
                    left: 0,
                    top: 0,
                    // A button centres its text; the video's names sit left, by the avatar.
                    textAlign: "left",
                    ...(state === "right" ? { transform: "scale(1.08)", background: wr(win) } : {}),
                    ...(state === "wrong" || state === "out" ? { opacity: 0.22, transform: "scale(0.94)" } : {}),
                  }}
                  onPointerDown={keep}
                  onClick={(event) => {
                    keep(event);
                    if (tap) return;
                    setTap({ option, ...tapPoint(event, i, drop) });
                  }}
                >
                  <Avatar name={option} inline />
                  <b style={{ flex: "1 1 0", minWidth: 0 }}>
                    {/* Fit again once the number takes its share of the pill. */}
                    <Fit key={state === "right" ? "lit" : "plain"} size={66}>
                      {option}
                    </Fit>
                  </b>
                  {state === "right" && parts.n ? <em>{parts.n}</em> : null}
                </button>
              </div>
            );
          })}
        </Zone>
        {tap ? (
          <Reveal
            pills={pills.current}
            picked={shown.indexOf(tap.option)}
            right={shown.indexOf(answer)}
            tap={tap}
            win={win}
            top={TOP + (shown.length - 1) * STEP + PILL + 76}
            next={{ bg: look.fg, fg: look.bg }}
            onNext={onDone}
          >
            <ResultLine got={tap.option === answer} mine={answer === me} answer={answer} {...parts} />
          </Reveal>
        ) : null}
      </Scene>
    </div>
  );
}

function ResultLine({ got, mine, answer, n, unit }: { got: boolean; mine: boolean; answer: string; n: string | null; unit: string }) {
  const said = got ? (mine ? "That's you." : `You got it. ${answer},`) : mine ? "Not quite. That's you," : `Not quite. It's ${answer},`;
  const end = /[.!?]$/.test(unit) ? "" : ".";
  if (!n) return <>{`${said} ${unit}${end}`}</>;
  return (
    <>
      {/* "That's you." ends its sentence; the others run on into the number. */}
      {said.endsWith(".") ? `${said} ` : `${said} with `}
      <b>{n}</b> {unit}
      {end}
    </>
  );
}

/**
 * The answer, played from the tap: a wrong pick shakes first, then the others
 * fade, the right pill grows into its colour with its number, and the result
 * line and the Next control rise.
 */
function Reveal({
  pills,
  picked,
  right,
  tap,
  win,
  top,
  next,
  onNext,
  children,
}: {
  pills: (HTMLButtonElement | null)[];
  picked: number;
  right: number;
  tap: Tap;
  win: string;
  top: number;
  next: { bg: string; fg: string };
  onNext: () => void;
  children: ReactNode;
}) {
  const root = useTimeline((s) => {
    const wrong = picked !== right;
    const at = wrong ? 0.5 : 0;
    const all = pills.filter((el): el is HTMLButtonElement => Boolean(el));
    const winner = pills[right];
    const chosen = pills[picked];
    const others = all.filter((el) => el !== winner);

    // fromTo paints its start at once, so the pills show their pre-answer look from the first frame.
    if (others.length) {
      s.tl.fromTo(
        others,
        { autoAlpha: 1, scale: 1 },
        { autoAlpha: 0.22, scale: 0.94, duration: 0.4, ease: "power2.out" },
        at + 0.4,
      );
    }
    if (winner) {
      // GSAP can't blend var() colours, so it blends their values; without them the colour just lands.
      const [white, lit] = [tokenValue("white"), tokenValue(win)];
      const colour = white && lit ? [{ backgroundColor: white }, { backgroundColor: lit }] : [{}, {}];
      s.tl.fromTo(
        winner,
        { scale: wrong ? 1 : 0.96, ...colour[0] },
        { scale: 1.08, ...colour[1], duration: 0.5, ease: "back.out(2.4)" },
        at + 0.4,
      );
      const em = winner.querySelector("em");
      if (em) s.tl.fromTo(em, { autoAlpha: 0, x: 40 }, { autoAlpha: 1, x: 0, duration: 0.4 }, at + 0.55);
    }
    if (chosen) {
      s.tl.fromTo(chosen, { scale: 1 }, { scale: 0.96, duration: 0.1, ease: "power1.in" }, 0);
      if (wrong) {
        s.tl.to(chosen, { scale: 1, duration: 0.3, ease: "back.out(3)" }, 0.1);
        s.shake(chosen, 0.1);
      }
    }
    s.sound(wrong ? "wrong" : "right", wrong ? 0.1 : 0.4);

    // The finger's ripple, where it actually landed.
    s.tl.fromTo("[data-part=dot]", { x: tap.x, y: tap.y, scale: 1, autoAlpha: 1 }, { scale: 0.8, duration: 0.1, ease: "power1.in" }, 0);
    s.tl.to("[data-part=dot]", { autoAlpha: 0, scale: 1, duration: 0.3, ease: "power1.out" }, 0.15);
    s.tl.fromTo("[data-part=ring]", { x: tap.x, y: tap.y, scale: 0.5, autoAlpha: 1 }, { scale: 2.6, autoAlpha: 0, duration: 0.6, ease: "power2.out" }, 0);

    s.rise("[data-part=result]", at + 0.85, { y: 24, sound: false });
    s.rise("[data-part=next]", at + 1.15, { y: 24, sound: false });
  });

  return (
    <div ref={root} className="contents">
      <Zone at="bottom">
        <div className="abs" style={{ left: 72, right: 72, top }}>
          <div data-part="result" className="line" style={{ position: "relative", left: 0, right: 0 }}>
            {children}
          </div>
          <button
            type="button"
            data-part="next"
            className="disp"
            style={{
              marginTop: 44,
              padding: "28px 64px",
              borderRadius: 999,
              background: next.bg,
              color: next.fg,
              fontSize: 56,
              lineHeight: 1,
            }}
            onPointerDown={keep}
            onClick={(event) => {
              keep(event);
              onNext();
            }}
          >
            Next →
          </button>
        </div>
      </Zone>
      {/* The tap point is measured on the whole stage, so the ripple takes no shift. */}
      <Zone at="top">
        <div data-part="dot" className="tap" aria-hidden />
        <div data-part="ring" className="tap-ring" aria-hidden />
      </Zone>
    </div>
  );
}
